import { useState, type ReactNode } from 'react'
import RiveComponent, { RiveFit } from '../../../../../../../components/RiveComponent'
import { useWebViewPageVisibility } from '../../../../../../../hooks/useWebViewPageVisibility'
import { postCTA } from '../../../../../../../utils/appBridge'
import { PlanCountdownSegments } from './PlanCountdownSegments'
import type { TimerCountdownChrome } from './timerCountdownChrome'
import type { PlanCountdownParts } from './usePlanCountdown'
import { PlanEducationIcon, type PlanEducationIconId } from './planEducationIcons'
import {
  TimerDelayCard,
  TimerNotifyBanner,
  TimerStrategyCta,
  TimerUnlockCta,
  type StrategyCtaVariant
} from './TimerShellPieces'

const STRATEGY_PANEL_RIVE_SRC = '/rives/allocation/responsive_allocation.riv'

type TimerPanelShellProps = {
  /** Chrome gradient (or solid) — applied as CSS `background`, not `backgroundColor`. */
  background: string
  title: string
  description: string
  hero: ReactNode
  footer: ReactNode
}

/** Shared layout + title/description typography for all plan timer panels. */
export function TimerPanelShell({
  background,
  title,
  description,
  hero,
  footer
}: TimerPanelShellProps) {
  return (
    <div>
      <div className="px-5 pt-8 pb-8" style={{ background }}>
        <div className="text-[22px] font-[500] leading-[130%] text-[#232323]">{title}</div>
        <div className="pt-3 text-[14px] font-[400] leading-[150%] text-[#3D3D3D]">{description}</div>
        {hero}
      </div>
      <div className="bg-white px-5 pt-9 pb-40">{footer}</div>
    </div>
  )
}

export function TimerPanelFooterHeading({ children }: { children: ReactNode }) {
  return <h2 className="pb-[13px] text-[22px] font-[500] leading-[130%] text-[#232323]">{children}</h2>
}

export function TimerPanelFooterCopy({ children }: { children: ReactNode }) {
  return <div className="pb-6 text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">{children}</div>
}

/**
 * Strategy CTA shell. Opens the questionnaire via native `strategyCta`, then
 * refetches plan status when the webview resumes (status may have changed).
 */
export function TimerPanelStrategySection({
  promptClassName,
  strategyCta,
  refetchPlan,
  variant = 'find',
  analyticsScreen
}: {
  promptClassName: string
  strategyCta?: unknown
  refetchPlan: () => void
  variant?: StrategyCtaVariant
  analyticsScreen?: string
}) {
  const [awaitingResume, setAwaitingResume] = useState(false)

  useWebViewPageVisibility({
    enabled: awaitingResume,
    onResume: () => {
      setAwaitingResume(false)
      refetchPlan()
    }
  })

  return (
    <>
      <div className="mt-6 h-[260px] w-full overflow-hidden rounded-2xl bg-white">
        <RiveComponent
          src={STRATEGY_PANEL_RIVE_SRC}
          fit={RiveFit.LAYOUT}
          autoplay
          className="h-full w-full"
        />
      </div>
      <TimerStrategyCta
        promptClassName={promptClassName}
        variant={variant}
        analyticsScreen={analyticsScreen}
        onFindStrategy={() => {
          if (strategyCta == null) return
          // Arm resume listener only after a real open — questionnaire may update planStatus
          setAwaitingResume(true)
          postCTA(JSON.stringify(strategyCta))
        }}
      />
    </>
  )
}

export function TimerPanelCountdownSection({
  readyIn,
  countdownParts,
  digitClassName,
  unitLabelClassName,
  shadowClassName,
  isDelayed,
  delayTitle,
  delayBody,
  notify,
  isSubscriptionEligible,
  unlockPrompt,
  unlockButtonLabel,
  trustedByClassName,
  analyticsScreen,
  landingPage
}: TimerCountdownChrome & {
  countdownParts: PlanCountdownParts
  isDelayed: boolean
  isSubscriptionEligible: boolean
  unlockButtonLabel: string
  analyticsScreen?: string
  landingPage?: 'elite' | 'prime'
}) {
  return (
    <>
      <div className={`mb-3 pt-6 text-[14px] font-[500] leading-[150%] ${readyIn.className}`}>
        {readyIn.text}
      </div>
      <PlanCountdownSegments
        countdownParts={countdownParts}
        digitClassName={digitClassName}
        unitLabelClassName={unitLabelClassName}
        shadowClassName={shadowClassName}
      />
      {isDelayed ? (
        <TimerDelayCard title={delayTitle} body={delayBody} shadowClassName={shadowClassName} />
      ) : null}
      {!isDelayed && isSubscriptionEligible ? (
        <TimerNotifyBanner notify={notify} shadowClassName={shadowClassName} />
      ) : null}
      {!isSubscriptionEligible ? (
        <TimerUnlockCta
          prompt={unlockPrompt}
          buttonLabel={unlockButtonLabel}
          trustedByClassName={trustedByClassName}
          analyticsScreen={analyticsScreen}
          landingPage={landingPage}
        />
      ) : null}
    </>
  )
}

export function TimerPanelFeatureRows({
  rows,
  iconColor
}: {
  rows: readonly { iconId: PlanEducationIconId; title: string; caption: string }[]
  iconColor: string
}) {
  return (
    <div className="flex flex-col gap-4">
      {rows.map((row) => (
        <div key={row.title} className="flex items-center gap-[14px]">
          <div className="-mt-[7px] h-6 w-6 shrink-0" aria-hidden>
            <PlanEducationIcon iconId={row.iconId} color={iconColor} className="h-6 w-6" />
          </div>
          <div className="text-[14px] leading-[150%] text-[#3D3D3D]">
            <span className="font-[500]">{row.title}</span>{' '}
            <span className="text-[#6A6E6E]">{row.caption}</span>
          </div>
        </div>
      ))}
    </div>
  )
}
